use App\Base\Enums\ButtonColor;
<script setup lang="ts">
type TaskData = App.Tasks.Data.TaskData;

import Check from '@/icons/line/check.svg';
import Edit from '@/icons/line/edit-alt.svg';
import Trash from '@/icons/line/trash.svg';
import Times from '@/icons/line/times.svg';
import TimesCircle from '@/icons/line/times-circle.svg';
import ExclamationTriangle from '@/icons/line/exclamation-triangle.svg';
import UsdCircle from '@/icons/line/usd-circle.svg';
import University from '@/icons/line/university.svg';
import AwardAlt from '@/icons/line/award-alt.svg';
import File from '@/icons/line/file.svg';
import Link from '@/icons/line/link.svg';
import { formatRelative } from '@/utils/date';

import Dialog from '@/components/dialog/Dialog.vue';
import Badge from '@/components/badge/Badge.vue';
import Button from '@/components/button/Button.vue';
import Alert from '@/components/alert/Alert.vue';
import DangerDialog from '@/components/dialog/DangerDialog.vue';
import { ButtonColor } from '@/hooks/useButtonClasses';
import AiAdviserButton from '@/domains/student-app/components/adviser/AiAdviserButton.vue';
import { useChatBot } from '@/domains/chat/composables/useChatBot';

const props = defineProps<{
  task: TaskData;
}>();

const { chatBotName, enableChatBotForStudents } = useChatBot();

const { show, closeLocally } = useDialog();

function getIcon(icon: string | null | undefined): Component | undefined {
  switch (icon) {
    case 'edit':
      return Edit;
    case 'times':
      return Times;
    case 'trash':
      return Trash;
    case 'check':
      return Check;
    default:
      return undefined;
  }
}

const taskTypeIcon = computed(() => {
  const taskType =
    props.task.type.value === 'data_request'
      ? props.task.data_request?.requestable_type.value
      : props.task.type.value;

  switch (taskType) {
    case 'financial_aid':
      return UsdCircle;
    case 'survey':
      return File;
    case 'application':
      return University;
    case 'college_application':
      return University;
    case 'student_scholarship':
      return AwardAlt;
    case 'scholarship':
      return AwardAlt;
    case 'test':
      return File;
    default:
      return undefined;
  }
});

const taskTypeLabel = computed(() => {
  return props.task.type.value === 'data_request'
    ? props.task.data_request?.requestable_type.label
    : props.task.type.label;
});

const isDismissing = ref(false);
const isDangerDialogOpen = ref(false);

function handleDismiss() {
  if (!props.task.dismiss_button?.href) {
    return closeLocally();
  }

  if (props.task.status.value === 'pending' && !isDangerDialogOpen.value) {
    isDangerDialogOpen.value = true;
    return;
  }

  if (['rejected', 'pending'].includes(props.task.status.value)) {
    const method = props.task.status.value === 'rejected' ? 'post' : 'delete';
    isDismissing.value = true;
    router[method](props.task.dismiss_button.href, {
      hooks: {
        success: () => {
          closeLocally();
          isDangerDialogOpen.value = false;
        },
        after: () => {
          isDismissing.value = false;
        }
      }
    });
    return;
  }

  router.navigate({ url: props.task.dismiss_button.href });
}

function handleConfirm() {
  const button = props.task.completion_button;
  const href = button?.href;
  if (!href) {
    return closeLocally();
  }

  if (button?.isExternalLink) {
    window.open(href, '_blank', 'noopener');
    return closeLocally();
  }

  return router.navigate({ url: href });
}

const footerHelperText = computed(() => {
  if (props.task.status.value === 'incomplete') {
    return props.task.prompt_title ?? undefined;
  }

  return undefined;
});
</script>


					<script lang="ts">
					import __hybridly_layout_0 from '~/resources/layouts/student-parent.vue';
					export default { layout: [__hybridly_layout_0] }
					</script>
					<template>
				
  <Dialog
    title="Task Details"
    :isOpen="show"
    :footerHelperText="footerHelperText"
    :cancelButtonLabel="task.dismiss_button?.title || null"
    :cancelButtonDisabled="task.dismiss_button?.isDisabled || false"
    :cancelButtonIconLeft="getIcon(task.dismiss_button?.iconLeft)"
    :cancelButtonColor="task.dismiss_button?.color ?? undefined"
    :cancelButtonLoading="isDismissing"
    :confirmButtonLabel="task.completion_button?.title"
    :confirmButtonDisabled="task.completion_button?.isDisabled || false"
    :confirmButtonColor="task.completion_button?.color ?? 'primary'"
    :confirmButtonIconLeft="getIcon(task.completion_button?.iconLeft)"
    :confirmButtonVariant="task.completion_button?.variant ?? undefined"
    :hideFooter="!task.dismiss_button && !task.completion_button"
    fit="fill"
    @onClose="closeLocally"
    @onCancel="handleDismiss"
    @onConfirm="handleConfirm"
  >
    <template #header>
      <div class="flex items-center gap-x-4">
        <div class="text-base font-semibold leading-5 text-slate-900">Task Details</div>
        <Badge
          v-if="
            task.status.value !== 'incomplete' &&
            task.status.value !== 'pending' &&
            task.status.value !== 'rejected'
          "
          :label="task.status.label"
          variant="soft"
          size="sm"
        />
      </div>
    </template>
    <template #task>
      <div class="flex flex-col gap-3 px-5 py-6">
        <h3 class="text-lg font-medium text-slate-900">{{ task.title }}</h3>
        <div class="flex flex-wrap items-center gap-2.5">
          <Badge :label="taskTypeLabel" variant="soft" :iconLeft="taskTypeIcon" />
          <div v-if="task.due_date" class="text-md text-slate-700">
            Due {{ formatRelative(task.due_date, 'MMMM d') }}
          </div>
        </div>
        <Alert
          v-if="
            task.status.value === 'rejected' ||
            (task.status.value === 'rejection_dismissed' &&
              task.data_request?.status.value === 'rejected')
          "
          title="Submission was not approved"
          :description="task.data_request?.rejected_reason ?? 'No reason provided'"
          variant="soft"
          color="danger"
          :icon="TimesCircle"
          alignment="horizontal"
          hideClose
        />
        <Alert
          v-if="task.due_date && task.due_date < new Date().toISOString()"
          title="This task is overdue"
          variant="soft"
          color="danger"
          :icon="ExclamationTriangle"
          alignment="horizontal"
          hideClose
        />
        <Alert
          v-if="
            task.due_date &&
            task.due_date < new Date(new Date().setDate(new Date().getDate() + 7)).toISOString() &&
            task.due_date > new Date().toISOString()
          "
          :title="`This task is due ${formatRelative(task.due_date, 'MMMM d')}`"
          variant="soft"
          color="warning"
          :icon="ExclamationTriangle"
          alignment="horizontal"
          hideClose
        />
        <Alert
          v-if="task.status.value === 'pending'"
          title="Submission is waiting for approval"
          variant="soft"
          color="warning"
          :icon="ExclamationTriangle"
          alignment="horizontal"
          hideClose
        />
      </div>
    </template>
    <template #chat>
      <div class="border-t border-slate-200 px-5 pb-10 pt-6">
        <h4 class="mb-2 text-base font-semibold leading-relaxed text-slate-900">
          {{ task.help_title }}
        </h4>
        <p v-if="task.help_text" class="mt-1 text-base leading-relaxed text-slate-600">
          {{ task.help_text }}
        </p>
        <div class="mt-6 flex gap-1.5" v-if="enableChatBotForStudents">
          <AiAdviserButton :label="`Ask ${chatBotName}`" isStacked />
          <Button
            size="md"
            variant="outlined"
            color="slate"
            :iconLeft="Link"
            :href="route('student.adviser.index')"
          >
            Contact adviser
          </Button>
        </div>
      </div>
    </template>
  </Dialog>
  <DangerDialog
    title="Delete Submission"
    message="Your submission will be deleted and cannot be recovered."
    confirmButtonLabel="Delete"
    :isOpen="isDangerDialogOpen"
    :isLoading="isDismissing"
    :confirmButtonColor="ButtonColor.danger"
    @onConfirm="handleDismiss"
    @onCancel="isDangerDialogOpen = false"
    @onClose="isDangerDialogOpen = false"
  />
</template>
